/**
 * ReengagementActionCell — one button per discharged-loan row.
 *
 * Shows the gate state at a glance (locked vs unlocked) with a progress tooltip,
 * and opens the ReengagementModal (log a call / send the email). Click is
 * stopPropagation'd so it doesn't toggle the row's expand. Type-checked
 * separately from the @ts-nocheck parent page.
 */

import { useState } from 'react';
import { Mail, Lock, Loader2 } from 'lucide-react';
import ReengagementModal from './ReengagementModal';
import type { LoanContactEntry, MutateContactInput } from '../../hooks/useLoanContacts';
import type {
  DischargedReengagementConfig,
  ReengagementEligibility,
} from '../../types/dischargedReengagement';

interface Props {
  companyId: string;
  loan: { loanId: string; customerName: string };
  loanBroker?: string;
  eligibility: ReengagementEligibility | undefined;
  config: DischargedReengagementConfig | null;
  loading?: boolean;
  contactEntries: LoanContactEntry[];
  onMutateContact: (input: MutateContactInput) => Promise<{ ok: boolean; entryId: string }>;
  availableBrokers: { id: string; name: string }[];
  currentUser: { uid?: string; displayName?: string; email?: string; role?: string };
  onChanged: () => void | Promise<void>;
}

export default function ReengagementActionCell({
  companyId,
  loan,
  loanBroker,
  eligibility,
  config,
  loading,
  contactEntries,
  onMutateContact,
  availableBrokers,
  currentUser,
  onChanged,
}: Props) {
  const [open, setOpen] = useState(false);

  const eligible = eligibility?.eligible === true;
  const minAttempts = eligibility?.minAttempts ?? config?.minAttempts ?? 4;
  const minWindowDays = eligibility?.minWindowDays ?? config?.minWindowDays ?? 21;
  const tooltip = eligible
    ? 'Ready to send the re-engagement email'
    : `${eligibility?.attemptCount ?? 0}/${minAttempts} attempts · ${
        eligibility?.windowSpanDays ?? 0
      }/${minWindowDays} days`;

  return (
    <>
      <button
        type="button"
        title={tooltip}
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-semibold transition-colors ${
          eligible
            ? 'border-green-500/50 bg-green-500/10 text-green-300 hover:bg-green-500/20'
            : 'border-gray-600 bg-gray-800 text-gray-400 hover:bg-gray-700'
        }`}
      >
        {loading ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : eligible ? (
          <Mail className="h-3.5 w-3.5" />
        ) : (
          <Lock className="h-3.5 w-3.5" />
        )}
        Re-engage
      </button>

      {open && (
        <ReengagementModal
          companyId={companyId}
          loan={loan}
          loanBroker={loanBroker}
          eligibility={eligibility}
          config={config}
          contactEntries={contactEntries}
          onMutateContact={onMutateContact}
          availableBrokers={availableBrokers}
          currentUser={currentUser}
          onClose={() => setOpen(false)}
          onChanged={onChanged}
        />
      )}
    </>
  );
}
